import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Divider, notification } from 'antd';
import moment from 'moment';

import HeaderWithHelp from '@/components/HeaderWithHelp';
import DashedContainer from '@/components/DashedContainer';
import TourExtraServices from '@/components/TourExtraServices';
import Loading from '@/components/Loading';
import WidgetCategory from '@/components/WidgetCategory';
import useTranslatedValidationConfig from '@/hooks/useTranslatedValidationConfig';
import useApiValidationsForForm from '@/hooks/useApiValidationsForForm';
import { formatExtraServicesForSubmit, eServicesFormat } from '@/helpers/mapping';
import { returnDeletedImages } from '@/helpers/deleteImages';
import { actions } from '@/redux/tour/cruise/create/reducer';
import { selectors } from '@/redux/tour/cruise/create/model';

import CruiseDetails from './CruiseDetails';
import CruisePrices from './CruisePrices';
import ImageUpload from './ImageUpload';
import baseValidations from './validations';

const formFields = [
    'images',
    'name',
    'types',
    'line',
    'start_date',
    'end_date',
    'departure_place',
    'arrival_place',
    'description',
    'adult_price',
    'adult_price_type',
    'cost',
    'cost_type',
];

const question = 'What is this?';
const answer = 'Lorem ipsum dolor sit amet...';

// format cruise type as initial data
const cruiseTypeFormat = (types) => types.map((type) => (
    type.id
));

const formatDataToSubmit = (id, categoryId, values, initialImages) => ({
    id,
    category: categoryId,
    name: values.name,
    types: values.types,
    line: values.line,
    start_date: values.start_date,
    end_date: values.end_date,
    departure_place: values.departure_place,
    arrival_place: values.arrival_place,
    description: values.description,

    adult_price: values.adult_price,
    adult_price_type: values.adult_price_type,
    cost: values.cost,
    cost_type: values.cost_type,
    widgets_category: values.widgets_category,

    images: [values.images?.[0]?.url],
    deleted_images: returnDeletedImages(initialImages, [values.images?.[0]]),
    extra_services: values.extra_services
        ? formatExtraServicesForSubmit(values.extra_services)
        : [],
});

const GeneralDetails = ({
    form,
    id,
    isEdit,
    regStep,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const validations = useTranslatedValidationConfig(baseValidations);
    const shipDetails = useSelector(selectors.shipDetails);

    const generalInfo = useSelector(selectors.generalInfo);
    const initialImages = generalInfo?.images;

    // add mode
    const onAdd = useCallback((values) => {
        const data = formatDataToSubmit(id, 3, values, initialImages);
        const step = 1;
        const completedStep = 0;
        dispatch(actions.requestSubmitCruiseGeneralDetails(
            data,
            step,
            completedStep,
        ));
    }, [dispatch]);

    // edit mode
    const onEdit = useCallback((values) => {
        const data = formatDataToSubmit(id, 3, values, initialImages);
        const step = 1;
        const completedStep = 1;
        dispatch(actions.requestSubmitCruiseGeneralDetails(
            data,
            step,
            completedStep,
        ));
    }, [dispatch]);

    const errors = useSelector(selectors.errors);

    useApiValidationsForForm({ form, errors, formFields });

    const typeError = useSelector(selectors.cruiseTypeError);
    useEffect(() => {
        if (typeError !== null) {
            notification.error({
                description: t(typeError),
                duration: 0,
            });
        }
        dispatch(actions.clearCruiseTypeError());
    }, [typeError]);

    const initialData = useMemo(() => {
        if (generalInfo) {
            return {
                name: generalInfo.name,
                types: generalInfo.types
                    ? cruiseTypeFormat(generalInfo.types)
                    : [],
                line: generalInfo.line,
                start_date: moment(generalInfo.start_date),
                end_date: moment(generalInfo.end_date),
                departure_place: generalInfo.departure_place,
                arrival_place: generalInfo.arrival_place,
                description: generalInfo.description,

                adult_price: generalInfo.adult_price,
                adult_price_type: (generalInfo.adult_price_type === ' Per Person') ? 1 : 6,
                cost: generalInfo.cost,
                cost_type: generalInfo.cost_type === '1' ? 1 : 2,
                widgets_category: generalInfo
                    .widget_categories.map((category) => category.id),
                extra_services: generalInfo.extra_services
                    ? eServicesFormat(generalInfo.extra_services)
                    : [],
                images: [{
                    id: generalInfo.images?.[0]?.id,
                    url: generalInfo.images?.[0]?.path,
                    thumbUrl: generalInfo.images?.[0]?.path,
                }],
            };
        } else {
            return {
                adult_price_type: 1,
                cost_type: 1,
            };
        }
    }, [generalInfo]);

    if (id && !initialData.name) {
        return <Loading />;
    }

    return (
        <Form
            form={form}
            id="cruise-general-details-form"
            layout="vertical"
            requiredMark={false}
            onFinish={
                (isEdit && regStep > 1)
                || (!isEdit && id)
                || (isEdit && shipDetails.length !== 0)
                    ? onEdit
                    : onAdd
            }
            initialValues={initialData || {}}
        >
            <HeaderWithHelp
                title="General Details"
                question={question}
                answer={answer}
            />

            <Divider />

            <Form.Item>
                <CruiseDetails validations={validations} />
            </Form.Item>

            <Form.Item>
                <CruisePrices validations={validations} />
            </Form.Item>

            <DashedContainer title="Widget Category">
                <Form.Item>
                    <WidgetCategory />
                </Form.Item>
            </DashedContainer>

            <DashedContainer title="Extra Services">
                <TourExtraServices
                    name="extra_services"
                    validations={validations}
                    checkboxText="The price is inclusive to the cruise"
                />
            </DashedContainer>

            <ImageUpload name="images" />
        </Form>
    );
};

export default GeneralDetails;
